/**
 * Razorpay Checkout — lazy loader.
 *
 * Replaces easebuzzLoader, and keeps the reason it existed: a payment SDK loaded
 * synchronously from index.html blocks HTML parsing, so anyone whose network
 * blocks the CDN got a blank panel rather than a slow one. This injects the
 * script only when a payment flow asks for it, and the failure is a sentence
 * about the network instead of a white screen.
 *
 * checkout.razorpay.com is rather less likely to be blocked than the S3 bucket
 * Easebuzz shipped from, but corporate DNS filters are creative and the same
 * protection costs nothing.
 */

const RAZORPAY_SRC = 'https://checkout.razorpay.com/v1/checkout.js';
const SCRIPT_TIMEOUT_MS = 12000;

let loaderPromise = null;

export function loadRazorpayCheckout() {
    if (typeof window === 'undefined') {
        return Promise.reject(new Error('Razorpay Checkout can only load in a browser'));
    }
    if (window.Razorpay) {
        return Promise.resolve(window.Razorpay);
    }
    if (loaderPromise) return loaderPromise;

    loaderPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = RAZORPAY_SRC;
        script.async = true;

        const timeout = setTimeout(() => {
            cleanup();
            loaderPromise = null;
            reject(new Error('The payment window could not be reached. Check your internet connection and try again.'));
        }, SCRIPT_TIMEOUT_MS);

        function cleanup() {
            clearTimeout(timeout);
            script.removeEventListener('load', onLoad);
            script.removeEventListener('error', onError);
        }

        function onLoad() {
            cleanup();
            if (window.Razorpay) {
                resolve(window.Razorpay);
            } else {
                // Loaded but did not register itself — usually a proxy serving
                // something that is not the script.
                loaderPromise = null;
                reject(new Error('The payment window loaded but did not start. Please refresh and try again.'));
            }
        }

        function onError() {
            cleanup();
            loaderPromise = null; // let the next click try again
            reject(new Error('Could not load the payment window. Check your internet connection and try again.'));
        }

        script.addEventListener('load', onLoad);
        script.addEventListener('error', onError);
        document.head.appendChild(script);
    });

    return loaderPromise;
}

/**
 * Open Checkout and resolve with what Razorpay hands back, or reject with a
 * reason a person can read.
 *
 * Resolving does not mean the money is ours — it means the browser believes the
 * payment went through. The server verifies the signature before anything is
 * credited, and the webhook is what settles it if this promise never resolves at
 * all because the tab was closed.
 *
 * @param {object} order   what the server returned from /initiate
 * @param {object} [opts]  { name, description, themeColor }
 */
export async function openRazorpayCheckout(order, opts = {}) {
    const Razorpay = await loadRazorpayCheckout();

    return new Promise((resolve, reject) => {
        let settled = false;
        const finish = (fn, arg) => { if (!settled) { settled = true; fn(arg); } };

        const rzp = new Razorpay({
            key: order.razorpayKeyId,
            order_id: order.razorpayOrderId,
            amount: order.amount,
            currency: order.currency,
            name: opts.name || 'Olli',
            description: opts.description || 'Payment',
            prefill: order.prefill || {},
            notes: { txnid: order.txnid },
            theme: { color: opts.themeColor || '#000000' },
            // Razorpay calls this with the ids and the signature. Everything that
            // matters is verified server-side; this is only the courier.
            handler: (response) => finish(resolve, response),
            modal: {
                // Closed without paying. Not an error worth shouting about — the
                // caller decides whether to say anything.
                ondismiss: () => finish(reject, Object.assign(
                    new Error('Payment window closed'), { dismissed: true }
                )),
            },
        });

        // A declined card, an expired UPI collect request. Razorpay keeps the
        // window open so the customer can try another method, so this does not
        // settle the promise — it only surfaces why the last attempt failed.
        rzp.on('payment.failed', (event) => {
            const d = event?.error?.description;
            if (d) console.warn('[razorpay] payment attempt failed:', d);
        });

        rzp.open();
    });
}
